import type {AppState} from "../../../data/state/AppState";
import type {IFilterRow} from "../../../data/models/filter/Filter";
import type {ISpotlightRule} from "../../../data/models/View";
import {FilterOperator} from "../../../data/models/filter/operator/FilterOperator";
import type {XyiconFeature} from "../../../generated/api/base";
import {FieldDataType} from "../../../generated/api/base";
import {FilterOperatorsV5} from "../filter/operator/FilterOperatorsV5";
import {BasicColors} from "../../../utils/BasicColors";
import {
	isConditionBasedSpotlightSupportedForField,
	isSpotlightSupportedForField,
} from "../../modules/space/spaceeditor/logic3d/renderers/SpaceViewRendererUtils";
import type {IFieldAdapter} from "../../../data/models/field/Field";
import {isHiddenViewColumnV5} from "../../../data/models/ViewUtils";

/**
 * Cleans spotlight rules for comparison by:
 * 1. Stripping ReactSortable properties (chosen, selected)
 * 2. Filtering out incomplete filters
 * 3. Removing trailing separators
 * 4. Filtering out incomplete rules
 * @param rules - Array of spotlight rules
 * @returns Cleaned rules ready for comparison
 */
export function cleanSpotlightRulesForComparison(rules: ISpotlightRule[]): Omit<ISpotlightRule, "label">[] {
	if (!rules) {
		return [];
	}

	return rules
		.map((rule) => {
			// Remove ReactSortable properties and label (computed from filters)
			// Note: expanded no longer exists on ISpotlightRule, but we strip it here in case of old data
			const {chosen, selected, label, expanded, ...cleanedRule} = rule as ISpotlightRule & {
				chosen?: boolean;
				selected?: boolean;
				expanded?: boolean;
			};

			if (cleanedRule.filters?.length > 0) {
				const completeFilters = cleanedRule.filters
					.filter((filter: any) => filter.type === "separator" || isFilterComplete(filter))
					.map((filter: any) => JSON.parse(JSON.stringify(filter)));

				while (completeFilters.length > 0 && completeFilters[completeFilters.length - 1]?.type === "separator") {
					completeFilters.pop();
				}

				return {...cleanedRule, filters: completeFilters};
			}

			return cleanedRule;
		})
		.filter((rule) => {
			if (rule.ruleType === "field") {
				return !!rule.fieldRefID;
			}
			if (rule.ruleType === "conditional") {
				return rule.filters?.some((f: any) => f.type === "filter");
			}
			return false;
		});
}

/**
 * Checks if an operator requires a parameter value
 * @param operator - The filter operator
 * @returns True if the operator needs a param, false otherwise
 */
export function operatorNeedsParam(operator: FilterOperator | string): boolean {
	if (!operator) {
		return true; // Default: assume param is needed
	}

	// Get operator config from FilterOperatorsV5
	const operatorConfig = FilterOperatorsV5.map[operator];

	// If operator has no control, it doesn't need a param
	// This covers: is_blank, is_not_blank, is_true, is_false,
	// is_today, is_yesterday, is_in_this_week, is_in_this_month, is_in_last_week, is_in_last_month
	return !!operatorConfig?.control;
}

/**
 * Checks if a filter is complete (has all required fields filled)
 * @param filter - The filter row to check
 * @returns True if the filter is complete, false otherwise
 */
export function isFilterComplete(filter: any): boolean {
	if (!filter || filter.type !== "filter") {
		return false;
	}

	const {field, operator, param} = filter.value || {};

	// Must have field and operator
	if (!field || !operator) {
		return false;
	}

	// Check if operator needs a param using the centralized helper
	if (!operatorNeedsParam(operator)) {
		return true; // Complete without param
	}

	// Operators that need params must have param filled
	return param !== null && param !== undefined && param !== "";
}

/**
 * Formats a date/time string to a human-readable format
 * @param dateString - ISO date string or date value
 * @param isTimeOnly - Whether to show only time
 * @returns Formatted date/time string
 */
function formatDateTime(dateString: string, isTimeOnly: boolean = false): string {
	if (!dateString) {
		return "?";
	}

	try {
		const date = new Date(dateString);
		if (isNaN(date.getTime())) {
			return String(dateString);
		}

		if (isTimeOnly) {
			// Format as time only (e.g., "2:30 PM")
			return date.toLocaleTimeString("en-US", {
				hour: "numeric",
				minute: "2-digit",
				hour12: true,
			});
		} else {
			// Format as date (e.g., "Nov 4, 2025")
			return date.toLocaleDateString("en-US", {
				year: "numeric",
				month: "short",
				day: "numeric",
			});
		}
	} catch (e) {
		return String(dateString);
	}
}

/**
 * Formats parameter value based on operator type
 * @param param - The parameter value
 * @param operatorConfig - The operator configuration
 * @returns Formatted parameter string
 */
function formatParamValue(param: any, operatorConfig: any): string {
	if (!operatorConfig) {
		return String(param || "Value");
	}

	// Operators without params (blank/not blank, true/false)
	if (!operatorConfig.control) {
		return "";
	}

	// Detect date/time operators
	const isDateOperator =
		operatorConfig.id === "is_equal_to_date" ||
		operatorConfig.id === "is_less_than_date" ||
		operatorConfig.id === "is_less_than_or_equal_to_date" ||
		operatorConfig.id === "is_greater_than_date" ||
		operatorConfig.id === "is_greater_than_or_equal_to_date";

	const isTimeOperator = operatorConfig.id === "is_at_time" || operatorConfig.id === "is_before_time" || operatorConfig.id === "is_after_time";

	const isBetweenDateOperator = operatorConfig.id === "is_in_between_date" || operatorConfig.id === "is_not_between_date";

	const isBetweenTimeOperator = operatorConfig.id === "is_between_time";

	const isBetweenNumOperator = operatorConfig.id === "is_in_between_num" || operatorConfig.id === "is_not_between_num";

	// Handle between operators - they use object format
	if (isBetweenNumOperator && param && typeof param === "object") {
		// BetweenParamV5 uses {min, max}
		if ("min" in param && "max" in param) {
			return `${param.min ?? "?"} and ${param.max ?? "?"}`;
		}
	}

	if (isBetweenDateOperator && param && typeof param === "object") {
		// BetweenDateParamV5 uses {from, to}
		if ("from" in param && "to" in param) {
			const fromDate = formatDateTime(param.from);
			const toDate = formatDateTime(param.to);
			return `${fromDate} and ${toDate}`;
		}
	}

	if (isBetweenTimeOperator && param && typeof param === "object") {
		// BetweenTimeParamV5 uses {from, to}
		if ("from" in param && "to" in param) {
			const fromTime = formatDateTime(param.from, true);
			const toTime = formatDateTime(param.to, true);
			return `${fromTime} and ${toTime}`;
		}
	}

	// Handle single date operators (DateParamV5)
	if (isDateOperator && param) {
		return formatDateTime(param);
	}

	// Handle single time operators (TimeParamV5)
	if (isTimeOperator && param) {
		return formatDateTime(param, true);
	}

	// Handle "any of" operators (AnyParamV5)
	const isAnyOfOperator = operatorConfig.id === "is_any_of" || operatorConfig.id === "is_not_any_of";

	if (isAnyOfOperator && Array.isArray(param)) {
		return param.length > 0 ? param.join(", ") : "Value";
	}

	// Handle explicit date operators (e.g., "In the last 5 days")
	const isExplicitDateOperator = operatorConfig.id === "is_in_the_last_date" || operatorConfig.id === "is_in_the_next_date";

	if (isExplicitDateOperator && typeof param === "object" && param?.value && param?.unit) {
		return `${param.value} ${param.unit}${param.value > 1 ? "s" : ""}`;
	}

	// Default: simple string conversion
	// Handle null/undefined/empty string
	if (param === null || param === undefined || param === "") {
		return "Value";
	}

	if (typeof param === "object" && !Array.isArray(param)) {
		console.warn("Unexpected param object structure:", param);
		return "Value";
	}

	return String(param);
}

/**
 * Constructs a human-readable title for spotlight rule filters
 * @param filters - Array of filter rows
 * @param appState - AppState instance for getting field titles
 * @returns Formatted filter string (e.g., "Field1 = Value1 AND Field2 > Value2")
 */
export function constructFilterTitle(filters: IFilterRow[], appState: AppState): string {
	if (!filters || filters.length === 0) {
		return "";
	}

	const parts: string[] = [];

	filters.forEach((filter) => {
		if (filter.type === "filter") {
			const fieldTitle = filter.value.field ? appState.actions.getFieldTitle(filter.value.field) : "Field";
			const operatorConfig = FilterOperatorsV5.map[filter.value.operator];
			let operatorLabel = operatorConfig?.label || "Condition";
			const fieldObj = filter.value.field ? appState.actions.getFieldByRefId(filter.value.field) : null;

			// Special-case Boolean fields to display the custom true/false labels from field settings
			if (fieldObj?.dataType === FieldDataType.Boolean && operatorConfig) {
				const settings = fieldObj.dataTypeSettings as any;
				const trueLabel = settings?.displayLabelForTrue || operatorLabel;
				const falseLabel = settings?.displayLabelForFalse || operatorLabel;

				if (operatorConfig.id === FilterOperator.IS_TRUE) {
					operatorLabel = trueLabel;
				} else if (operatorConfig.id === FilterOperator.IS_FALSE) {
					operatorLabel = falseLabel;
				}
			}

			const paramValue = formatParamValue(filter.value.param, operatorConfig);

			// Build the filter string - only add param if it exists
			if (paramValue) {
				parts.push(`${fieldTitle} ${operatorLabel} ${paramValue}`);
			} else {
				// For operators without params (is blank, is not blank)
				parts.push(`${fieldTitle} ${operatorLabel}`);
			}
		} else {
			// Separator (AND/OR)
			parts.push(filter.value);
		}
	});

	return parts.join(" ");
}

// Helper function to get default colors for new rules from BasicColors palette
export const getNextDefaultColor = (index: number): string => {
	const basicColors = BasicColors.getBasicColorsV5();
	const selectedColor = basicColors[index % basicColors.length];
	return `#${selectedColor.hex}`;
};

/**
 * Removes filters that reference deleted fields from spotlight rules
 * @param rules - Array of spotlight rules
 * @param appState - AppState instance to check if fields exist
 * @returns Cleaned rules with deleted field conditions removed
 */
export function removeDeletedFieldsFromRules(rules: ISpotlightRule[], appState: AppState): ISpotlightRule[] {
	if (!rules || !appState) {
		return rules || [];
	}

	return rules
		.map((rule) => {
			// For field-based rules, check if the main field still exists
			if (rule.ruleType === "field" && rule.fieldRefID) {
				const fieldExists = !!appState.actions.getFieldByRefId(rule.fieldRefID);
				if (!fieldExists) {
					// If main field is deleted, remove entire rule
					return null;
				}
			}
			// If filters reference deleted fields, clean them up
			if (rule.filters && rule.filters.length > 0) {
				const cleanedFilters = removeDeletedFieldsFromFilters(rule.filters, appState);

				// For conditional rules, if all filters were removed (only field in the rule was deleted),
				// remove the entire rule
				if (rule.ruleType === "conditional" && cleanedFilters.length === 0) {
					return null;
				}

				return {...rule, filters: cleanedFilters};
			}

			return rule;
		})
		.filter((rule): rule is ISpotlightRule => rule !== null);
}

/**
 * Removes filter conditions that reference deleted fields
 * Also cleans up orphaned separators
 * @param filters - Array of filter rows
 * @param appState - AppState instance to check if fields exist
 * @returns Cleaned filters array
 */
export function removeDeletedFieldsFromFilters(filters: IFilterRow[], appState: AppState): IFilterRow[] {
	if (!filters || filters.length === 0) {
		return [];
	}

	// First: mark filters with deleted fields for removal
	const validFilters: IFilterRow[] = [];

	for (let i = 0; i < filters.length; i++) {
		const filter = filters[i];

		if (filter.type === "filter") {
			const fieldRefId = filter.value?.field;
			if (fieldRefId) {
				const fieldExists = !!appState.actions.getFieldByRefId(fieldRefId);
				if (fieldExists) {
					validFilters.push(filter);
				}
				// If field doesn't exist, skip this filter (don't add it to the valid filters)
			} else {
				// No field set yet (incomplete filter), keep it in the valid filters
				validFilters.push(filter);
			}
		} else if (filter.type === "separator") {
			validFilters.push(filter);
		}
	}

	// Second: clean up orphaned separators
	// Remove leading separators
	while (validFilters.length > 0 && validFilters[0]?.type === "separator") {
		validFilters.shift();
	}

	// Remove trailing separators
	while (validFilters.length > 0 && validFilters[validFilters.length - 1]?.type === "separator") {
		validFilters.pop();
	}

	// Remove consecutive separators (keep only the first one)
	const finalFilters: IFilterRow[] = [];
	for (let i = 0; i < validFilters.length; i++) {
		const current = validFilters[i];
		const prev = finalFilters[finalFilters.length - 1];

		if (current.type === "separator" && prev?.type === "separator") {
			// Skip consecutive separator
			continue;
		}
		finalFilters.push(current);
	}

	return finalFilters;
}

export const filterFieldForSpotlight = (field: IFieldAdapter): boolean => {
	if (!isSpotlightSupportedForField(field)) {
		return false;
	}

	const refId = field.refId;
	if (isHiddenViewColumnV5(refId)) {
		return false;
	}

	return true;
};

export const filterFieldForConditionBasedSpotlight = (field: IFieldAdapter): boolean => {
	if (!isConditionBasedSpotlightSupportedForField(field)) {
		return false;
	}

	const refId = field.refId;
	if (isHiddenViewColumnV5(refId)) {
		return false;
	}

	return true;
};

export const getAllFieldsForSpotlight = (appState: AppState, feature: XyiconFeature) => {
	const fields = appState.actions.getFieldsByFeature(feature, true);

	return fields.filter(filterFieldForSpotlight);
};
